/* StarWays 2.0 — home / 6-staps booking flow */
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Minus, Plus, LocateFixed, Clock, Calendar } from "lucide-react";
import { SwHeader } from "../components/SwHeader";
import { SwFooter } from "../components/SwFooter";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const TOTAL_STEPS = 6;
const STORAGE_KEY = "sw_booking_v2";

/* ---------- Nominatim geocode (reused pattern) ---------- */
const geocode = async (q) => {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=nl,be&addressdetails=1&q=${encodeURIComponent(q)}`,
    { headers: { "Accept-Language": "nl" } }
  );
  if (!res.ok) throw new Error("Geen resultaat");
  return res.json();
};

/* ---------- Address field with inline suggestions ---------- */
const AddressField = ({ value, onPick, placeholder, testId, autoFocus }) => {
  const [q, setQ] = useState(value?.label || "");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setQ(value?.label || ""); }, [value?.label]);

  const search = (text) => {
    clearTimeout(timer.current);
    if (!text || text.length < 3) { setSug([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const data = await geocode(text);
        setSug(data || []);
        setOpen(true);
      } catch { setSug([]); }
    }, 300);
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        className="sw-input"
        data-testid={testId}
        value={q}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
        onFocus={() => sug.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && sug.length > 0 && (
        <ul
          data-testid={`${testId}-suggestions`}
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
            background: "var(--sw-white)", border: "1px solid var(--sw-line)",
            borderRadius: 16, listStyle: "none", padding: 6, margin: 0,
            maxHeight: 280, overflowY: "auto", zIndex: 10,
            boxShadow: "0 8px 24px rgba(18,36,29,0.08)",
          }}
        >
          {sug.map((s, i) => (
            <li key={s.place_id || i}>
              <button
                type="button"
                style={{
                  width: "100%", textAlign: "left", padding: "10px 14px",
                  background: "transparent", border: 0, cursor: "pointer",
                  borderRadius: 10, fontSize: 14, color: "var(--sw-ink)",
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const picked = {
                    label: s.display_name.split(",").slice(0, 2).join(", "),
                    full: s.display_name,
                    lat: parseFloat(s.lat), lng: parseFloat(s.lon),
                  };
                  onPick(picked); setQ(picked.label); setOpen(false);
                }}
              >
                {s.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ---------- Stepper (passengers) ---------- */
const Stepper = ({ value, onChange, min = 1, max = 8 }) => (
  <div
    role="group"
    aria-label="Personen"
    style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "var(--sw-mist)", borderRadius: 999, padding: 6 }}
  >
    <button
      type="button"
      data-testid="sw-pass-minus"
      aria-label="Minder personen"
      onClick={() => onChange(Math.max(min, value - 1))}
      disabled={value <= min}
      style={pillBtn}
    >
      <Minus size={16} />
    </button>
    <span data-testid="sw-pass-count" style={{ minWidth: 24, textAlign: "center", fontWeight: 600, color: "var(--sw-ink)" }}>
      {value}
    </span>
    <button
      type="button"
      data-testid="sw-pass-plus"
      aria-label="Meer personen"
      onClick={() => onChange(Math.min(max, value + 1))}
      disabled={value >= max}
      style={pillBtn}
    >
      <Plus size={16} />
    </button>
  </div>
);

const pillBtn = {
  width: 36, height: 36, borderRadius: 999,
  background: "var(--sw-white)", border: "1px solid var(--sw-line)",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", color: "var(--sw-ink)",
};

/* ---------- Nu/Later toggle ---------- */
const WhenToggle = ({ when, setWhen, scheduledAt, setScheduledAt }) => {
  const nowBtn = {
    flex: 1, minHeight: 48, borderRadius: 12,
    background: when === "now" ? "var(--sw-emerald)" : "var(--sw-mist)",
    color: when === "now" ? "var(--sw-white)" : "var(--sw-ink)",
    border: 0, fontWeight: 600, cursor: "pointer",
  };
  const laterBtn = { ...nowBtn, background: when === "later" ? "var(--sw-emerald)" : "var(--sw-mist)", color: when === "later" ? "var(--sw-white)" : "var(--sw-ink)" };

  return (
    <>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" style={nowBtn} data-testid="sw-when-now" onClick={() => setWhen("now")}>
          <Clock size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} /> Nu
        </button>
        <button type="button" style={laterBtn} data-testid="sw-when-later" onClick={() => setWhen("later")}>
          <Calendar size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} /> Later
        </button>
      </div>
      {when === "later" && (
        <input
          type="datetime-local"
          className="sw-input"
          data-testid="sw-scheduled-at"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
          style={{ marginTop: 12 }}
        />
      )}
    </>
  );
};

/* ---------- Step 1 ---------- */
const Step1 = ({ state, update, onNext }) => {
  const useCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
          { headers: { "Accept-Language": "nl" } }
        );
        const d = await r.json();
        update({ pickup: {
          label: d.display_name?.split(",").slice(0, 2).join(", ") || "Huidige locatie",
          full: d.display_name || "Huidige locatie",
          lat: latitude, lng: longitude,
        }});
      } catch {
        update({ pickup: { label: "Huidige locatie", full: "Huidige locatie", lat: latitude, lng: longitude }});
      }
    });
  };

  const canContinue = state.dropoff?.lat && state.pickup?.lat && (state.when === "now" || state.scheduledAt);

  return (
    <div className="sw-slide-right" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <label style={labelStyle}>Waar wil je heen?</label>
        <AddressField
          value={state.dropoff}
          onPick={(d) => update({ dropoff: d })}
          placeholder="Bestemming"
          testId="sw-dropoff"
          autoFocus
        />
        {state.dropoff?.label && (
          <div className="sw-chosen" style={{ fontSize: 22, marginTop: 8 }} data-testid="sw-dropoff-chosen">
            {state.dropoff.label}
          </div>
        )}
      </div>

      <div>
        <label style={labelStyle}>Vertrek</label>
        <AddressField
          value={state.pickup}
          onPick={(p) => update({ pickup: p })}
          placeholder="Ophaaladres"
          testId="sw-pickup"
        />
        <button
          type="button"
          onClick={useCurrentLocation}
          data-testid="sw-use-location"
          className="sw-chip"
          style={{ marginTop: 10 }}
        >
          <LocateFixed size={14} style={{ marginRight: 6 }} /> Huidige locatie
        </button>
      </div>

      <div>
        <label style={labelStyle}>Wanneer</label>
        <WhenToggle
          when={state.when}
          setWhen={(w) => update({ when: w })}
          scheduledAt={state.scheduledAt}
          setScheduledAt={(s) => update({ scheduledAt: s })}
        />
      </div>

      <div>
        <label style={labelStyle}>Personen</label>
        <Stepper value={state.passengers} onChange={(n) => update({ passengers: n })} />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
        <button type="button" className="sw-chip" data-testid="sw-chip-schiphol" onClick={async () => {
          const d = await geocode("Schiphol Airport");
          if (d?.[0]) update({ dropoff: {
            label: "Schiphol",
            full: d[0].display_name,
            lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon),
          }});
        }}>
          Schiphol
        </button>
        <button type="button" className="sw-chip" data-testid="sw-chip-amsterdam" onClick={async () => {
          const d = await geocode("Amsterdam Centraal");
          if (d?.[0]) update({ dropoff: {
            label: "Amsterdam Centraal",
            full: d[0].display_name,
            lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon),
          }});
        }}>
          Amsterdam Centraal
        </button>
      </div>

      <p style={{ fontSize: 13, color: "var(--sw-ink-soft)", margin: "4px 0 0" }}>
        Prijs vooraf. Geen verrassingen.
      </p>

      <button
        type="button"
        className="sw-btn sw-btn--block"
        data-testid="sw-step1-next"
        onClick={onNext}
        disabled={!canContinue}
        style={{ marginTop: 8 }}
      >
        Toon prijzen <ArrowRight size={18} />
      </button>
    </div>
  );
};

const labelStyle = {
  display: "block",
  marginBottom: 8,
  fontSize: 15,
  color: "var(--sw-ink-soft)",
  fontFamily: "'Bricolage Grotesque', sans-serif",
  fontWeight: 400,
};

/* Map (leaflet) only needed from step 4 — keep it out of the first bundle */
const RideAside = lazy(() => import("../components/booking/RideAside"));
const Step2Car = lazy(() => import("../components/booking/Step2Car"));
const Step3Theme = lazy(() => import("../components/booking/Step3Theme"));
const Step4Summary = lazy(() => import("../components/booking/Step4Summary"));
const Step5Pay = lazy(() => import("../components/booking/Step5Pay"));
const Step6Done = lazy(() => import("../components/booking/Step6Done"));
const SwAuthDialog = lazy(() => import("../components/booking/SwAuthDialog"));

/* ---------- Main ---------- */
const emptyState = {
  pickup: null,
  dropoff: null,
  when: "now",
  scheduledAt: "",
  passengers: 1,
  tier: "premium",
  theme: null,
  notes: "",
  bookingId: null,
  bookingSig: null,
};

const WIDE_STEPS = new Set([4, 5]);

const HomePage = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const stepParam = parseInt(params.get("stap") || "1", 10);
  const step = Number.isFinite(stepParam) && stepParam >= 1 && stepParam <= TOTAL_STEPS ? stepParam : 1;
  const [state, setState] = useState(() => {
    try { return { ...emptyState, ...(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}")) }; }
    catch { return emptyState; }
  });
  const [authOpen, setAuthOpen] = useState(false);
  const { quote, loading: quoteLoading } = useQuote(state, step >= 2 && step <= 5);

  useEffect(() => {
    document.body.classList.add("sw-shell");
    const t = setTimeout(() => {
      import("../components/booking/Step2Car");
      import("../components/booking/Step3Theme");
    }, 2500);
    return () => { clearTimeout(t); document.body.classList.remove("sw-shell"); };
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const update = (patch) => setState((s) => ({ ...s, ...patch }));

  const goto = (n, extra = {}) => {
    const next = Math.min(TOTAL_STEPS, Math.max(1, n));
    const sp = new URLSearchParams(params);
    sp.set("stap", String(next));
    Object.entries(extra).forEach(([k, v]) => (v == null ? sp.delete(k) : sp.set(k, v)));
    setParams(sp, { replace: false });
    if (typeof window !== "undefined" && window.__starwaysGaLoaded && typeof window.gtag === "function") {
      window.gtag("event", `stap_${next}_gezien`, { step: next });
    }
  };

  // Guard: steps 2-5 need a route
  const hasRoute = !!(state.pickup?.lat && state.dropoff?.lat);
  useEffect(() => {
    if (step >= 2 && step <= 5 && !hasRoute) goto(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, hasRoute]);

  // Return from Google login during checkout
  useEffect(() => {
    if (!user) return;
    let pending = null;
    try { pending = sessionStorage.getItem("pending_booking"); } catch { /* ignore */ }
    if (pending) {
      sessionStorage.removeItem("pending_booking");
      if (hasRoute) goto(5);
    }
    if (user && authOpen) setAuthOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Booking completed — clear the draft
  useEffect(() => {
    if (step === 6 && params.get("rit")) setState(emptyState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const wide = WIDE_STEPS.has(step);

  return (
    <>
      <SwHeader onOpenAuth={() => setAuthOpen(true)} />
      {step === 1 ? (
        <HeroBand>
          <div className="sw-card sw-card--xl sw-hero-card" data-testid="sw-step-card-1">
            <ProgressRow step={step} onBack={() => goto(step - 1)} />
            <Step1 state={state} update={update} onNext={() => goto(2)} />
          </div>
        </HeroBand>
      ) : (
        <main className="sw-container sw-flow" style={{ maxWidth: wide ? 1080 : 600 }}>
          <ProgressRow step={step} onBack={() => goto(step - 1)} />
          <div className={wide ? "sw-flow__grid" : ""}>
            <div className="sw-card sw-card--xl" data-testid={`sw-step-card-${step}`} key={step}>
              <Suspense fallback={<div className="sw-loading" style={{ minHeight: 240 }} />}>
              {step === 2 && <Step2Car state={state} update={update} quote={quote} loading={quoteLoading} onNext={() => goto(3)} />}
              {step === 3 && <Step3Theme state={state} update={update} quote={quote} onNext={() => goto(4)} />}
              {step === 4 && <Step4Summary state={state} update={update} goto={goto} onNext={() => goto(5)} />}
              {step === 5 && <Step5Pay state={state} update={update} quote={quote} />}
              {step === 6 && <Step6Done bookingId={params.get("rit")} onNewRide={() => { setState(emptyState); goto(1, { rit: null }); }} />}
              </Suspense>
            </div>
            {wide && hasRoute && (
              <Suspense fallback={<div className="sw-aside" style={{ minHeight: 320 }} />}>
                <RideAside state={state} quote={quote} compact={step === 5} />
              </Suspense>
            )}
          </div>
        </main>
      )}
      <SwFooter />
      {authOpen && (
        <Suspense fallback={null}>
          <SwAuthDialog open={authOpen} onOpenChange={setAuthOpen} />
        </Suspense>
      )}
    </>
  );
};

const STEP_NAMES = ["Route", "Auto", "Thema", "Overzicht", "Betalen", "Bevestigd"];

const ProgressRow = ({ step, onBack }) => (
  <div className="sw-progress-row">
    <button
      type="button"
      aria-label="Terug"
      data-testid="sw-step-back"
      onClick={onBack}
      disabled={step <= 1 || step === 6}
      className="sw-round-btn"
    >
      <ArrowLeft size={16} />
    </button>
    <div style={{ flex: 1 }}>
      <div className="sw-progress" data-testid="sw-progress">
        <div className="sw-progress__bar" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
      </div>
      <div style={{ fontSize: 12, color: "var(--sw-ink-soft)", marginTop: 6 }} data-testid="sw-step-label">
        Stap {step} van {TOTAL_STEPS} · {STEP_NAMES[step - 1]}
      </div>
    </div>
  </div>
);

/* Photo layer lives in public/index.html (#sw-boot) so it paints before JS — fast LCP */
const useHeroPhoto = () => {
  useEffect(() => {
    const el = document.documentElement;
    el.classList.add("sw-home-boot");
    return () => el.classList.remove("sw-home-boot");
  }, []);
};

const HeroBand = ({ children }) => {
  useHeroPhoto();
  return (
  <section className="sw-hero-band" data-testid="sw-hero-band">
    <div className="sw-hero-band__inner">
      <div className="sw-hero" data-testid="sw-home-hero">
        <h1 className="sw-hero__title" data-testid="sw-home-hero-title">
          <span style={{ fontWeight: 400 }}>Onze standaard is</span> <span style={{ fontWeight: 800, display: "block" }}>comfort.</span>
        </h1>
        <p className="sw-hero__sub" data-testid="sw-home-hero-sub">Jij kiest de rit, wij regelen de rest.</p>
      </div>
      {children}
    </div>
  </section>
  );
};

/* Fetch price quote whenever the route or party size changes */
const useQuote = (state, enabled) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = `${state.pickup?.lat},${state.pickup?.lng},${state.dropoff?.lat},${state.dropoff?.lng},${state.passengers}`;
  useEffect(() => {
    if (!enabled || !state.pickup?.lat || !state.dropoff?.lat) return;
    let alive = true;
    setLoading(true);
    api.post("/route", {
      pickup_lat: state.pickup.lat, pickup_lng: state.pickup.lng,
      dropoff_lat: state.dropoff.lat, dropoff_lng: state.dropoff.lng,
      passengers: state.passengers, tier: "comfort",
      pickup_address: state.pickup.full || state.pickup.label,
      dropoff_address: state.dropoff.full || state.dropoff.label,
    }).then(({ data }) => alive && setQuote(data))
      .catch(() => alive && setQuote(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  return { quote, loading };
};

export default HomePage;
